import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { cooperplaceApi } from 'services/api';
import { useSnackbar } from 'v3/components/Snackbar';
import { useModal } from 'hooks';
import Text from 'v3/components/Text';
import { fetchAutomations } from 'v3/pages/Load/Register/Automations/controller';
import { NegotiationsProvider } from './context';
import { Details } from './Pages/Details';

function normalizeNegotiation(raw) {
  return {
    ...raw,
    active: Boolean(raw.active),
    autoRegistrationValidation: Boolean(raw.autoRegistrationValidation),
    autoGroValidation: Boolean(raw.autoGroValidation),
    autoArrivalByRadius: Boolean(raw.autoArrivalByRadius),
    autoCreateOccurrenceArrivalByRadius: Boolean(raw.autoCreateOccurrenceArrivalByRadius),
    autoRegistrationValidationDays: raw.autoRegistrationValidationDays ?? null,
    autoGroValidationDays: raw.autoGroValidationDays ?? null,
    protocolDocs: Boolean(raw.protocolDocs),
    generateDeliveryProof: Boolean(raw.generateDeliveryProof),
    integracoes: raw.automations || raw.integracoes || [],
    autoCreateOccurrence: Boolean(raw.autoCreateOccurrence),
    occurrenceTypes: raw.occurrenceTypes || [],
  };
}

const OCCURRENCE_TYPES = [
  'ARRIVAL_AT_CLIENT_COLLECT',
  'ARRIVAL_AT_CLIENT_WAITING_UNLOADING',
  'TRANSIT'
]

export default function Negotiations() {
  const params = useParams();
  const snackbar = useSnackbar();
  const confirmDeactivateModal = useModal();

  const [negotiation, setNegotiation] = useState({});
  const [editData, setEditData] = useState({
    autoRegistrationValidation: false,
    autoGroValidation: false,
    autoArrivalByRadius: false,
    autoCreateOccurrenceArrivalByRadius: false,
    autoRegistrationValidationDays: null,
    autoGroValidationDays: null,
    protocolDocs: false,
    generateDeliveryProof: false,
    integracoes: [],
    autoCreateOccurrence: false,
    occurrenceTypes: [],
  });
  const [loading, setLoading] = useState(false);
  const [automationOptions, setAutomationOptions] = useState([]);
  const [occurrenceTypeOptions, setOccurrenceTypeOptions] = useState([]);

  function showSnackbar(text, options) {
    snackbar.show(<Text>{text}</Text>, options);
  }

  function seedEditData(normalized) {
    setEditData({
      autoRegistrationValidation: normalized.autoRegistrationValidation,
      autoGroValidation: normalized.autoGroValidation,
      autoArrivalByRadius: normalized.autoArrivalByRadius,
      autoCreateOccurrenceArrivalByRadius: normalized.autoCreateOccurrenceArrivalByRadius,
      autoRegistrationValidationDays: normalized.autoRegistrationValidationDays,
      autoGroValidationDays: normalized.autoGroValidationDays,
      protocolDocs: normalized.protocolDocs,
      generateDeliveryProof: normalized.generateDeliveryProof,
      integracoes: normalized.integracoes || [],
      autoCreateOccurrence: normalized.autoCreateOccurrence,
      occurrenceTypes: normalized.occurrenceTypes || [],
    });
  }

  async function fetchNegotiation() {
    setLoading(true);
    try {
      const { data } = await cooperplaceApi.get(`negotiation/${params.id}`);
      const normalized = normalizeNegotiation(data.data ?? data);
      setNegotiation(normalized);
      seedEditData(normalized);
    } catch {
      showSnackbar('Não foi possível carregar a negociação', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  async function getOccurrencesTypesOptions() {
    try {
      const response = await cooperplaceApi.get('occurrences/types');
      const findOcc = response?.data?.message.filter(occ => OCCURRENCE_TYPES.includes(occ.slug));
      setOccurrenceTypeOptions(findOcc);
    } catch (error) {
      setOccurrenceTypeOptions([]);
    }
  }

  async function updateNegotiation() {
    setLoading(true);
    try {
      const { data } = await cooperplaceApi.patch(`negotiation/${params.id}`, {
        autoRegistrationValidation: editData.autoRegistrationValidation,
        autoGroValidation: editData.autoGroValidation,
        autoArrivalByRadius: editData.autoArrivalByRadius,
        autoCreateOccurrenceArrivalByRadius: editData.autoCreateOccurrenceArrivalByRadius,
        autoRegistrationValidationDays: editData.autoRegistrationValidation
          ? editData.autoRegistrationValidationDays
          : 0,
        autoGroValidationDays: editData.autoGroValidation
          ? editData.autoGroValidationDays
          : 0,
        protocolDocs: editData.protocolDocs,
        generateDeliveryProof: editData.generateDeliveryProof,
        automationIds: editData.integracoes.map(a => a.id),
        autoCreateOccurrence: editData.autoCreateOccurrence,
        occurrenceTypeIds: editData.autoCreateOccurrence
          ? editData.occurrenceTypes.map(t => t.id)
          : [],
      });
      const normalized = normalizeNegotiation(data.data);
      setNegotiation(normalized);
      seedEditData(normalized);
      showSnackbar('Negociação atualizada com sucesso!', { type: 'success' });
    } catch (err) {
      const message = err?.response?.data?.error || 'Erro ao atualizar negociação';
      showSnackbar(message, { type: 'error' });
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function activateNegotiation() {
    try {
      const { data } = await cooperplaceApi.patch(`negotiation/${params.id}/activate`);
      setNegotiation(normalizeNegotiation(data.data));
      showSnackbar('Negociação ativada com sucesso!', { type: 'success' });
    } catch (err) {
      const message = err?.response?.data?.error || 'Erro ao ativar negociação';
      showSnackbar(message, { type: 'error' });
    }
  }

  async function deactivateNegotiation() {
    confirmDeactivateModal.close();
    try {
      const { data } = await cooperplaceApi.patch(`negotiation/${params.id}/deactivate`);
      setNegotiation(normalizeNegotiation(data.data));
      showSnackbar('Negociação desativada com sucesso!', { type: 'success' });
    } catch (err) {
      const message = err?.response?.data?.error || 'Erro ao desativar negociação';
      showSnackbar(message, { type: 'error' });
    }
  }

  useEffect(() => {
    if (params.id) {
      fetchNegotiation();
    }
  }, [params.id]);

  useEffect(() => {
    fetchAutomations().then(setAutomationOptions);
    getOccurrencesTypesOptions();
  }, []);

  return (
    <NegotiationsProvider
      value={{
        negotiation,
        editData,
        setEditData,
        loading,
        showSnackbar,
        updateNegotiation,
        activateNegotiation,
        deactivateNegotiation,
        confirmDeactivateModal,
        seedEditData,
        automationOptions,
        occurrenceTypeOptions,
      }}
    >
      <Details />
    </NegotiationsProvider>
  );
}
